import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Phone, Link as LinkIcon, Building, Headphones, MapPin, ArrowLeft, Calendar, Clock, FileEdit as Edit } from 'lucide-react';
import { motion } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import StatusDropdown from '../components/ui/StatusDropdown';
import AppointmentEditForm from '../components/AppointmentEditForm';
import CallSection from '../components/CallSection';
import EdenredPanel from '../components/EdenredPanel';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../lib/auth';
import { updateAppointmentStatus } from '../lib/api';
import { formatDateTime } from '../lib/utils';

const AppointmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isAuthenticated } = useAuth();
  const [appointment, setAppointment] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [backUrl, setBackUrl] = useState('/appointments');
  const [backLabel, setBackLabel] = useState('Retour aux rendez-vous');

  useEffect(() => {
    // Déterminer l'URL de retour basée sur l'état de navigation ou les paramètres URL
    const searchParams = new URLSearchParams(location.search);
    const fromClient = searchParams.get('from');
    
    if (fromClient) {
      setBackUrl(`/clients/${fromClient}`);
      setBackLabel('Retour au client');
    } else if (location.state?.from) {
      // Si on vient d'une page client via state
      const fromPath = location.state.from;
      if (fromPath.startsWith('/clients/')) {
        setBackUrl(fromPath);
        setBackLabel('Retour au client');
      }
    }
  }, [location]);

  useEffect(() => {
    if (!id || !profile || !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const fetchAppointment = async () => {
      setIsLoading(true);
      setError(null);
      setNotFound(false);

      try {
        const { data, error } = await supabase
          .from('appointments')
          .select(`
            id, title, status, date, time, phone, location, description, notes,
            prospect_name, prospect_linkedin,
            company_name, company_linkedin,
            call_id, recording_path, job_title, client_id, sdr_id,
            clients ( id, name ),
            profiles!appointments_sdr_id_fkey ( user_id, sales_name )
          `)
          .eq('id', id)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          setNotFound(true);
          return;
        }

        setAppointment(data);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Erreur de chargement du rendez-vous');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointment();
  }, [id, profile, isAuthenticated]);

  const handleRecordingUploaded = (recordingPath: string) => {
    setAppointment(prev => ({
      ...prev,
      recording_path: recordingPath
    }));
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;

    try {
      const updatedAppointment = await updateAppointmentStatus(id, newStatus);
      setAppointment(updatedAppointment);
    } catch (error) {
      console.error('Error updating appointment status:', error);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="bg-white rounded-2xl p-6 space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center">
              <Calendar className="h-12 w-12 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Rendez-vous introuvable</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              Le rendez-vous que vous recherchez n'existe pas ou a été supprimé.
            </p>
            <Link to={backUrl}>
              <Button variant="outline" icon={<ArrowLeft className="h-4 w-4" />}>
                {backLabel}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center">
              <Calendar className="h-12 w-12 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Erreur de chargement</h3>
            <p className="text-sm text-red-500 mb-6">{error}</p>
            <Link to={backUrl}>
              <Button variant="outline" icon={<ArrowLeft className="h-4 w-4" />}>
                {backLabel}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return null;
  }

  if (isEditing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
                Modifier le rendez-vous
              </h1>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <AppointmentEditForm
                appointment={appointment}
                onSave={(updated) => {
                  setAppointment(updated);
                  setIsEditing(false);
                }}
                onCancel={() => setIsEditing(false)}
              />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header avec navigation */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <Link 
              to={backUrl}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Link>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">
                    {appointment.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 mt-3">
                    <div className="flex items-center text-lg font-semibold text-blue-600">
                      <Calendar className="h-5 w-5 mr-2" />
                      <span>{formatDateTime(appointment.date, appointment.time)}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Building className="h-4 w-4 mr-2" />
                      <span className="font-medium">Client: {appointment.clients?.name}</span>
                    </div>
                    {appointment.profiles && (
                      <div className="flex items-center text-gray-600">
                        <Headphones className="h-4 w-4 mr-2" />
                        <span className="font-medium">SDR: {appointment.profiles.sales_name}</span>
                      </div>
                    )}
                    <StatusDropdown
                      currentStatus={appointment.status}
                      onChange={handleStatusChange}
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => setIsEditing(true)} 
                  variant="outline"
                  icon={<Edit className="h-4 w-4" />}
                  className="flex-shrink-0"
                >
                  Modifier
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="space-y-6">
            {/* Informations du prospect */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <Card title="Informations du prospect" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
                <div className="space-y-4">
                  {/* Ligne 1: Contact et Poste */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 mb-1">Contact</p>
                        {appointment.prospect_linkedin ? (
                          <a 
                            href={appointment.prospect_linkedin} 
                            className="text-blue-600 hover:text-blue-800 font-medium" 
                            target="_blank" 
                            rel="noreferrer"
                          >
                            {appointment.prospect_name}
                          </a>
                        ) : (
                          <p className="text-gray-900 font-medium">{appointment.prospect_name}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Building className="h-5 w-5 text-purple-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 mb-1">Poste</p>
                        <p className="text-gray-900 font-medium">
                          {appointment.job_title || 'Non spécifié'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ligne 2: Téléphone et Entreprise */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {appointment.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                          <a 
                            href={`tel:${appointment.phone}`} 
                            className="text-green-600 hover:text-green-800 font-medium"
                          >
                            {appointment.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-3">
                      <Building className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 mb-1">Entreprise</p>
                        {appointment.company_linkedin ? (
                          <a 
                            href={appointment.company_linkedin} 
                            className="text-indigo-600 hover:text-indigo-800 font-medium" 
                            target="_blank" 
                            rel="noreferrer"
                          >
                            {appointment.company_name}
                          </a>
                        ) : (
                          <p className="text-gray-900 font-medium">{appointment.company_name}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Ligne 3: Lieu (si présent) */}
                  {appointment.location && (
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-500 mb-1">Lieu du rendez-vous</p>
                          <p className="text-gray-900 font-medium">{appointment.location}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>

            {/* Enregistrement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
            >
              <Card title="Enregistrement de l'appel" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
                <CallSection
                  callId={appointment.call_id}
                  supabaseRecordingPath={appointment.recording_path}
                  appointmentId={appointment.id}
                  onRecordingUploaded={handleRecordingUploaded}
                />
              </Card>
            </motion.div>

            {/* Description */}
            {appointment.description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card title="Description" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {appointment.description}
                  </p>
                </Card>
              </motion.div>
            )}

            {appointment.notes && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Card title="Notes" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {appointment.notes}
                  </p>
                </Card>
              </motion.div>
            )}

            {appointment.client_id === 'd0c36153-a122-4c1e-bdeb-8a4918be123e' &&
             (profile?.role === 'admin' || profile?.role === 'super_admin') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <EdenredPanel
                  appointment={appointment}
                  onUpdate={setAppointment}
                />
              </motion.div>
            )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailPage;